import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { departmentsApi, employees, hireOrders, positionsApi, workSchedulesApi, calcTypesApi,
  EMPLOYMENT_KIND_LABELS, type EmploymentKind } from '@/api/hr';
import { orgs } from '@/api/catalogs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import PlannedAccrualsTable from '@/components/PlannedAccrualsTable';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useFormValidation } from '@/hooks/useFormValidation';
import { useFormDraft } from '@/shared/hooks/useFormDraft';

interface Form {
  organization_id: string;
  document_number: string;
  document_date: string;
  employee_id: string;
  hire_date: string;
  position_id: string;
  department_id: string;
  employment_kind: EmploymentKind;
  salary_amount: string;
  probation_days: string;
  contract_basis: string;
  work_schedule_id: string;
  calc_type_id: string;
  comment: string;
}

const today = new Date();
const EMPTY: Form = {
  organization_id: '',
  document_number: '',
  document_date: today.toISOString().slice(0, 10),
  employee_id: '',
  hire_date: today.toISOString().slice(0, 10),
  position_id: '', department_id: '',
  employment_kind: 'MAIN',
  salary_amount: '0',
  probation_days: '',
  contract_basis: '',
  work_schedule_id: '',
  calc_type_id: '',
  comment: '',
};

export default function HireOrderEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const [search] = useSearchParams();
  const isNew = !id || id === 'new';
  const empFromUrl = search.get('employee');

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр org dropdown по payroll.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('payroll.manage');
  const docQuery = useQuery({
    queryKey: ['hire-order', id],
    queryFn: () => hireOrders.get(id!),
    enabled: !isNew,
  });
  const [form, setForm] = useState<Form>(EMPTY);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const numberPreview = useNumberPreview('doc.hire_orders', form.organization_id, isNew);
  const v = useFormValidation();

  function validate(): boolean {
    return v.check([
      ['organization_id', form.organization_id, 'Оберіть організацію'],
      ['document_date', form.document_date, 'Вкажіть дату наказу'],
      ['employee_id', form.employee_id, 'Оберіть співробітника'],
      ['hire_date', form.hire_date, 'Вкажіть дату прийому'],
    ]);
  }
  function onSave() {
    if (!validate()) return;
    isNew ? create.mutate() : update.mutate();
  }
  // Сторінка створення: зберегти чернетку і одразу провести (create → post → картка).
  async function onSaveAndPost() {
    if (!validate()) return;
    setError(null);
    try {
      const doc = await create.mutateAsync();
      await hireOrders.post(doc.id);
      qc.invalidateQueries({ queryKey: ['hire-orders'] });
      qc.invalidateQueries({ queryKey: ['employees-full'] });
      navigate(`/hire-orders/${doc.id}`);
    } catch (e) {
      setError(extractError(e));
    }
  }
  // Збережений непроведений: зберегти поточні правки і одразу провести (update → post).
  async function onUpdateAndPost() {
    if (!validate()) return;
    setError(null);
    try {
      await update.mutateAsync();
      await hireOrders.post(id!);
      qc.invalidateQueries({ queryKey: ['hire-order', id] });
      qc.invalidateQueries({ queryKey: ['hire-orders'] });
      qc.invalidateQueries({ queryKey: ['employees-full'] });
    } catch (e) {
      setError(extractError(e));
    }
  }
  const [error, setError] = useState<string | null>(null);

  const empListQ = useQuery({
    queryKey: ['employees-not-hired', form.organization_id],
    queryFn: () => employees.list({
      organization_id: form.organization_id,
      limit: 1000,
    }),
    enabled: !!form.organization_id,
  });
  const posQ = useQuery({ queryKey: ['positions'], queryFn: () => positionsApi.list({ limit: 500 }) });
  const schedQ = useQuery({ queryKey: ['work-schedules'], queryFn: () => workSchedulesApi.list() });
  const calcQ = useQuery({ queryKey: ['calc-types'], queryFn: calcTypesApi.list });
  const depQ = useQuery({
    queryKey: ['departments', form.organization_id],
    queryFn: () => departmentsApi.list({ organization_id: form.organization_id, limit: 500 }),
    enabled: !!form.organization_id,
  });

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f, organization_id: firstOrg,
        employee_id: empFromUrl ?? f.employee_id,
      });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 10),
        employee_id: d.employee_id,
        hire_date: d.hire_date,
        position_id: d.position_id ?? '',
        department_id: d.department_id ?? '',
        employment_kind: d.employment_kind ?? 'MAIN',
        salary_amount: String(d.salary_amount),
        // Fallback для імпортних наказів з 1С (несуть probation_months, не days):
        // показуємо probation_months × 30 днів (рішення власника 2026-06-15).
        probation_days:
          d.probation_days != null ? String(d.probation_days)
          : d.probation_months != null ? String(d.probation_months * 30)
          : '',
        contract_basis: d.contract_basis ?? '',
        work_schedule_id: d.work_schedule_id ?? '',
        calc_type_id: d.calc_type_id ?? '',
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data, availableOrgs, empFromUrl]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  // Список незанятых сотрудников (без hire_date или с dismiss_date).
  const eligibleEmps = (empListQ.data?.items ?? []).filter((e) =>
    !e.hire_date || e.dismiss_date
  );
  // Обраний у документі співробітник ЗАВЖДИ в опціях (2026-08-17): після
  // проведення наказу він отримує hire_date і випадає з фільтра «не
  // прийняті» — без цього проведений док показував порожнє поле.
  const selectedEmp = (empListQ.data?.items ?? [])
    .find((e) => e.id === form.employee_id);
  const empOptions = selectedEmp
    && !eligibleEmps.some((e) => e.id === selectedEmp.id)
    ? [selectedEmp, ...eligibleEmps] : eligibleEmps;

  const create = useMutation({
    mutationFn: () => hireOrders.create({
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      employee_id: form.employee_id,
      hire_date: form.hire_date,
      position_id: form.position_id || null,
      department_id: form.department_id || null,
      employment_kind: form.employment_kind,
      salary_amount: form.salary_amount,
      probation_days: form.probation_days ? Number(form.probation_days) : null,
      contract_basis: form.contract_basis || null,
      work_schedule_id: form.work_schedule_id || null,
      calc_type_id: form.calc_type_id || null,
      comment: form.comment || null,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['hire-orders'] });
      navigate(`/hire-orders/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });

  const update = useMutation({
    mutationFn: () => hireOrders.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      hire_date: form.hire_date,
      position_id: form.position_id || null,
      department_id: form.department_id || null,
      employment_kind: form.employment_kind,
      salary_amount: form.salary_amount,
      probation_days: form.probation_days ? Number(form.probation_days) : null,
      contract_basis: form.contract_basis || null,
      work_schedule_id: form.work_schedule_id || null,
      calc_type_id: form.calc_type_id || null,
      comment: form.comment || null,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hire-order', id] }),
    onError: (e) => setError(extractError(e)),
  });

  const unpost = useMutation({
    mutationFn: () => hireOrders.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hire-order', id] });
      qc.invalidateQueries({ queryKey: ['hire-orders'] });
      qc.invalidateQueries({ queryKey: ['employees-full'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => hireOrders.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hire-orders'] });
      navigate('/hr-orders');
    },
  });

  // UX β: warning якщо новий документ а user не має payroll.manage у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: 'var(--badge-amber-bg)', borderRadius: 6,
            border: '1px solid #fbbf24', color: 'var(--badge-amber-fg)',
            maxWidth: 600, margin: '1rem auto' }}>
            ⚠ У вас немає прав створювати наказ про прийом на роботу у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/hr-orders')}>Скасувати</button>
        </div>
      </div>
    );
  }

  // ч.877 (новий дизайн, Закон #10): «ще дій» не було. Прогрес — обовʼязкові:
  // організація, дата, співробітник, дата прийому.
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'emp', title: 'Співробітник і умови прийому', required: 2,
      filled: (form.employee_id ? 1 : 0) + (form.hire_date ? 1 : 0) },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий наказ (прийом на роботу)' : `Наказ (прийом на роботу) № ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          onBackToList={() => navigate('/hr-orders')}
          onSave={onSave}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={isNew ? onSaveAndPost : onUpdateAndPost}
          postPending={create.isPending || update.isPending}
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { confirm('Видалити?').then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
          onPrint={() => navigate(`/hire-orders/${id}/print`)}
          entriesDoc={{ type: 'doc.hire_orders', id: isNew ? '' : id! }}
          railMode
          extraActions={railActions}
        />
        </div>
      </div>

      {v.banner()}

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.hire_orders', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація *</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>№ наказу</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
                 disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Дата наказу *</label>
          <DatePopover value={form.document_date}
                 onChange={(val) => setForm({ ...form, document_date: val })}
                 disabled={isPosted} />
        </div>
      </div>
      </DocBlock>

      <DocBlock index={2} title="Співробітник і умови прийому">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 8' }}>
          <label>Співробітник *</label>
          <select value={form.employee_id}
                  onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">— оберіть співробітника —</option>
            {empOptions.map((e) => (
              <option key={e.id} value={e.id}>
                {e.full_name}{e.code ? ` — ${e.code}` : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Прийняти з дати *</label>
          <DatePopover value={form.hire_date}
                 onChange={(val) => setForm({ ...form, hire_date: val })}
                 disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Посада</label>
          <select value={form.position_id}
                  onChange={(e) => setForm({ ...form, position_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">—</option>
            {posQ.data?.items.map((p) => (
              <option key={p.id} value={p.id}>{p.name} — {p.code}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Підрозділ</label>
          <select value={form.department_id}
                  onChange={(e) => setForm({ ...form, department_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">—</option>
            {depQ.data?.items.map((d) => (
              <option key={d.id} value={d.id}>{d.name} — {d.code}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Форма зайнятості</label>
          <select value={form.employment_kind}
                  onChange={(e) => setForm({ ...form, employment_kind: e.target.value as EmploymentKind })}
                  disabled={isPosted}>
            {(Object.entries(EMPLOYMENT_KIND_LABELS) as Array<[EmploymentKind, string]>)
              .map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Оклад, грн</label>
          <input type="number" step="0.01" value={form.salary_amount}
                 onChange={(e) => setForm({ ...form, salary_amount: e.target.value })}
                 disabled={isPosted}
                 style={{ textAlign: 'right' }} />
        </div>
        {docQuery.data?.planned_accruals?.length ? (
          <PlannedAccrualsTable lines={docQuery.data.planned_accruals} />
        ) : null}
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Випробування, днів</label>
          <input type="number" min={0} value={form.probation_days}
                 onChange={(e) => setForm({ ...form, probation_days: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Графік роботи</label>
          <select value={form.work_schedule_id}
                  onChange={(e) => setForm({ ...form, work_schedule_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">—</option>
            {(schedQ.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Вид розрахунку</label>
          <select value={form.calc_type_id}
                  onChange={(e) => setForm({ ...form, calc_type_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">—</option>
            {(calcQ.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 12' }}>
          <label>Підстава (трудовий договір)</label>
          <input value={form.contract_basis}
                 onChange={(e) => setForm({ ...form, contract_basis: e.target.value })}
                 disabled={isPosted}
                 placeholder="наприклад: Трудовий договір №1 від 15.01.2026" />
        </div>
      </div>
      </DocBlock>

      <DocBlock index={3} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.hire_orders" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error">{error}</div>}

      {!isPosted && (
        <p style={{ marginTop: '1rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
          При проведенні: створиться запис у регістрі історії співробітника
          з вказаною посадою/підрозділом/окладом. У картці співробітника
          з'явиться дата прийому. Бухгалтерських проводок документ не робить.
        </p>
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
